/**
 * Lightweight SEO head manager.
 *
 * Why not react-helmet-async?
 *   We don't want to add a dependency just to set `<title>`, a handful of
 *   meta tags, and a JSON-LD script. This component does it with a small
 *   useEffect and React 18's createPortal directly into <head>.
 *
 * Usage:
 *   <SEOHead
 *     title="JSON Formatter Online — Free JSON Beautifier | DevKit"
 *     description="Format, beautify and validate JSON online. ..."
 *     canonical="https://dditzy.com/json-formatter"
 *     og={{ title, description, image, url, type }}
 *     twitter={{ card, title, description, image }}
 *     jsonLd={[{ '@context': ..., '@type': ... }, ...]}
 *   />
 */

import { useEffect } from 'react';

export interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string[];
  canonical?: string;
  og?: {
    title?: string;
    description?: string;
    image?: string;
    url?: string;
    type?: 'website' | 'article';
  };
  twitter?: {
    card?: 'summary' | 'summary_large_image';
    title?: string;
    description?: string;
    image?: string;
  };
  noIndex?: boolean;
  /** Array of JSON-LD objects. Each becomes its own <script type="application/ld+json">. */
  jsonLd?: Array<Record<string, unknown>>;
}

function setMeta(name: string, content: string, attr: 'name' | 'property' = 'name') {
  if (!content) return;
  const selector = `meta[${attr}="${name}"]`;
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function removeMeta(name: string, attr: 'name' | 'property' = 'name') {
  const el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${name}"]`);
  if (el) el.remove();
}

function setLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function setJsonLd(entries: Array<Record<string, unknown>>) {
  // Wipe previously injected DevKit JSON-LD blocks (they have data-devkit-seo)
  document.head
    .querySelectorAll('script[type="application/ld+json"][data-devkit-seo]')
    .forEach((el) => el.remove());

  entries.forEach((entry) => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-devkit-seo', 'true');
    script.text = JSON.stringify(entry);
    document.head.appendChild(script);
  });
}

export function SEOHead({
  title,
  description,
  keywords,
  canonical,
  og,
  twitter,
  noIndex,
  jsonLd,
}: SEOHeadProps) {
  useEffect(() => {
    document.title = title;

    setMeta('description', description);

    if (keywords && keywords.length) {
      setMeta('keywords', keywords.join(', '));
    } else {
      removeMeta('keywords');
    }

    if (canonical) setLink('canonical', canonical);

    if (noIndex) {
      setMeta('robots', 'noindex, nofollow');
    } else {
      removeMeta('robots');
    }

    // Open Graph
    setMeta('og:title', og?.title ?? title, 'property');
    setMeta('og:description', og?.description ?? description, 'property');
    setMeta('og:type', og?.type ?? 'website', 'property');
    if (og?.url) setMeta('og:url', og.url, 'property');
    if (og?.image) setMeta('og:image', og.image, 'property');

    // Twitter
    setMeta('twitter:card', twitter?.card ?? 'summary_large_image');
    setMeta('twitter:title', twitter?.title ?? title);
    setMeta('twitter:description', twitter?.description ?? description);
    if (twitter?.image) setMeta('twitter:image', twitter.image);

    // JSON-LD
    if (jsonLd && jsonLd.length) setJsonLd(jsonLd);

    return () => {
      // Clean up JSON-LD blocks on unmount so a navigation away doesn't leave them.
      document.head
        .querySelectorAll('script[type="application/ld+json"][data-devkit-seo]')
        .forEach((el) => el.remove());
    };
  }, [title, description, keywords?.join(','), canonical, og?.url, og?.image, noIndex, jsonLd]);

  return null;
}